import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import AdminUserList from './AdminUserList';
import AdminInviteCodeManager from './AdminInviteCodeManager';
import AdminActivityLog from './AdminActivityLog';
import AdminPendingReview from './AdminPendingReview';
import api from '../../services/api';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [stats, setStats] = useState(null);
  const { user } = useAuth();
  const { isDark } = useTheme();
  
  useEffect(() => {
    fetchStats();
  }, []);
  
  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      // Stats loaded
    }
  };
  
  // Check if user has admin role
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }
  
  const endImpersonation = () => {
    // Clear impersonation token and restore original
    const originalToken = localStorage.getItem('originalToken');
    if (originalToken) {
      localStorage.setItem('accessToken', originalToken);
      localStorage.removeItem('originalToken');
      localStorage.removeItem('impersonationToken');
      window.location.href = '/admin';
    }
  };
  
  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header with Impersonation Indicator */}
      {user?.isImpersonation && (
        <div className="glass-alert-warning p-4 mb-6 flex justify-between items-center rounded-lg">
          <span className="text-mono">
            Impersonating: {user.email} (by {user.impersonatedByEmail})
          </span>
          <button onClick={endImpersonation} className="btn-glass text-sm px-4 py-2">
            End Impersonation
          </button>
        </div>
      )}
      
      <div className="mb-8">
        <h1 className={`heading-mono text-hierarchy-1 mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Admin Dashboard
        </h1>
        <p className={`text-mono ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Manage users, invite codes, and platform settings
        </p>
      </div>
      
      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-4">
            <h3 className="text-mono text-sm text-gray-400 mb-1">Total Users</h3>
            <p className="text-2xl font-bold text-white">{stats.total_users}</p>
          </div>
          <div className="glass-card p-4">
            <h3 className="text-mono text-sm text-gray-400 mb-1">Active (7d)</h3>
            <p className="text-2xl font-bold text-green-400">{stats.active_users_7d}</p>
          </div>
          <div className="glass-card p-4">
            <h3 className="text-mono text-sm text-gray-400 mb-1">Total Searches</h3>
            <p className="text-2xl font-bold text-accent-400">{stats.total_searches}</p>
          </div>
          <div className="glass-card p-4">
            <h3 className="text-mono text-sm text-gray-400 mb-1">Active Codes</h3>
            <p className="text-2xl font-bold text-yellow-400">{stats.active_invite_codes}</p>
          </div>
        </div>
      )}
      
      {/* Tab Navigation */}
      <div className="glass-card p-0 mb-6 rounded-xl overflow-hidden">
        <div className="flex">
          {['pending', 'users', 'invites', 'activity'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-4 text-mono capitalize transition-all ${
                activeTab === tab 
                  ? 'bg-accent-400/10 border-b-2 border-accent-400 text-accent-400' 
                  : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="animate-fade-in-scale">
        {activeTab === 'pending' && <AdminPendingReview />}
        {activeTab === 'users' && <AdminUserList />}
        {activeTab === 'invites' && <AdminInviteCodeManager />}
        {activeTab === 'activity' && <AdminActivityLog />}
      </div>
    </div>
  );
};

export default AdminDashboard;